import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import { createPortal } from 'react-dom';
import mapboxgl, {
  Marker as MarkerInstanceBase,
  MarkerOptions
} from 'mapbox-gl';
import Point from '@mapbox/point-geometry';
import { isEqual } from 'lodash-es';

import { color } from '@strava/ui-tokens/js';
import { LatLng, LngLatObj } from './types';
import { useMap } from './useMap';
import { getClassNameSet } from './utils';

export type MarkerEvent = {
  type: 'dragstart' | 'drag' | 'dragend';
  target: MarkerInstance;
  lngLat: LngLatObj;
};

export type MarkerEventListener = (e: MarkerEvent) => void;

export interface MarkerInstance extends MarkerInstanceBase {
  // TODO: once this type is added to `@types/mapbox-gl` remove it here
  /**
   * Returns the current occludedOpacity of the marker.
   *
   */
  getOccludedOpacity(): number;

  // TODO: once this type is added to `@types/mapbox-gl` remove it here
  /**
   * Sets the occludedOpacity property of the marker. This opacity is used on the marker
   * when the marker is occluded by terrain.
   */
  setOccludedOpacity(opacity: number): this;
}

export interface MarkerProps extends Omit<MarkerOptions, 'element'> {
  className?: string;

  // TODO: once this type is added to `@types/mapbox-gl` remove it here
  /**
   * The opacity of a marker that's occluded by 3D terrain. Value between 0 and 1.
   */
  occludedOpacity?: number;

  /**
   * Coordinates of the marker.
   */
  latLng: LatLng;

  /**
   * Called when dragging starts.
   */
  onDragStart?: MarkerEventListener;

  /**
   * Called while dragging.
   */
  onDrag?: MarkerEventListener;

  /**
   * Called when dragging ends.
   */
  onDragEnd?: MarkerEventListener;

  children?: React.ReactNode;
}

const Marker = forwardRef<MarkerInstance, MarkerProps>((props, ref) => {
  const { className, latLng, children, ...mapboxMarkerOptions } = props;

  const map = useMap();

  const prevPropsRef = useRef<MarkerProps>(props);
  const classNameRef = useRef<string>();

  // Mapbox marker instance
  const marker = useMemo(
    () => {
      const hasChildren = React.Children.toArray(children).length > 0;

      const markerInstance = new mapboxgl.Marker({
        color: color.colorCoreO3,
        ...mapboxMarkerOptions,
        element: hasChildren ? document.createElement('div') : undefined
      }) as MarkerInstance;

      markerInstance.setLngLat([latLng[1], latLng[0]]);

      return markerInstance;
    },
    // As we only want to create mapboxgl.Marker once and as we manually update it's props/options,
    // we intentionally have empty dependency array and disabled the eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useImperativeHandle(ref, () => marker, [marker]);

  useEffect(
    () => {
      const handleDragStart = (e?: Object) => {
        const event = e as MarkerEvent;
        event.lngLat = event.target.getLngLat();
        prevPropsRef.current.onDragStart?.(event);
      };
      const handleDrag = (e?: Object) => {
        const event = e as MarkerEvent;
        event.lngLat = event.target.getLngLat();
        prevPropsRef.current.onDrag?.(event);
      };
      const handleDragEnd = (e?: Object) => {
        const event = e as MarkerEvent;
        event.lngLat = event.target.getLngLat();
        prevPropsRef.current.onDragEnd?.(event);
      };

      marker.on('dragstart', handleDragStart);
      marker.on('drag', handleDrag);
      marker.on('dragend', handleDragEnd);

      if (map) {
        marker.addTo(map);
      }

      return () => {
        marker.off('dragstart', handleDragStart);
        marker.off('drag', handleDrag);
        marker.off('dragend', handleDragEnd);
        marker.remove();
      };
    },
    // We only want to attach events once on initial render and as marker will not change in the
    // lifespan of this component we intentionally have empty dependency array and disabled the
    // eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  //
  // Handle updating class list
  //
  if (classNameRef.current !== className) {
    const prevClassNames = getClassNameSet(classNameRef.current);
    const newClassNames = getClassNameSet(className);

    prevClassNames.forEach((c) => {
      if (!newClassNames.has(c)) {
        marker.getElement().classList.remove(c);
      }
    });

    newClassNames.forEach((c) => {
      if (!prevClassNames.has(c)) {
        marker.getElement().classList.add(c);
      }
    });

    classNameRef.current = className;
  }

  //
  // Handle updating position
  //
  const [prevLat, prevLng] = prevPropsRef.current.latLng;
  const [lat, lng] = latLng;
  if (prevLat !== lat || prevLng !== lng) {
    marker.setLngLat([lng, lat]);
  }

  //
  // Update the offset property
  //
  if (
    mapboxMarkerOptions.offset !== undefined &&
    !isEqual(marker.getOffset(), Point.convert(mapboxMarkerOptions.offset))
  ) {
    marker.setOffset(mapboxMarkerOptions.offset);
  }

  //
  // Update the draggable property
  //
  if (
    mapboxMarkerOptions.draggable !== undefined &&
    marker.isDraggable() !== mapboxMarkerOptions.draggable
  ) {
    marker.setDraggable(mapboxMarkerOptions.draggable);
  }

  //
  // Update the rotation property
  //
  if (
    mapboxMarkerOptions.rotation !== undefined &&
    marker.getRotation() !== mapboxMarkerOptions.rotation
  ) {
    marker.setRotation(mapboxMarkerOptions.rotation);
  }

  //
  // Update the rotationAlignment property
  //
  if (
    mapboxMarkerOptions.rotationAlignment !== undefined &&
    marker.getRotationAlignment() !== mapboxMarkerOptions.rotationAlignment
  ) {
    marker.setRotationAlignment(mapboxMarkerOptions.rotationAlignment);
  }

  //
  // Update the pitchAlignment property
  //
  if (
    mapboxMarkerOptions.pitchAlignment !== undefined &&
    marker.getPitchAlignment() !== mapboxMarkerOptions.pitchAlignment
  ) {
    marker.setPitchAlignment(mapboxMarkerOptions.pitchAlignment);
  }

  //
  // Update the occludedOpacity property
  //
  if (
    mapboxMarkerOptions.occludedOpacity !== undefined &&
    marker.getOccludedOpacity() !== mapboxMarkerOptions.occludedOpacity
  ) {
    marker.setOccludedOpacity(mapboxMarkerOptions.occludedOpacity);
  }

  // This needs to be at the bottom so that we make sure prevProps gets updates after all the checks
  // and updates have been performed.
  prevPropsRef.current = props;

  // Fragment is used here to get around React types version mismatch between @strava/ui
  // and other product apps/packages.
  return <>{createPortal(children, marker.getElement())}</>;
});

export default Marker;
